import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import styled from '@mui/material/styles/styled';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { sentenceCase } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import Modal from '../../sections/modal/Modal';
import ModalButtons from '../../sections/modal/ModalButtons';

const StyledSpan = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderBottom: 'solid 2px black',
}));

const DeleteCollectionMenuItem = ({
  isSolrCloud,
  name,
  onClose,
  onRefresh,
}) => {
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [form, setForm] = useState('');
  const [modal, setModal] = useState(false);

  const handleFormChange = (e) => setForm(e.target.value);
  const toggleModal = () => setModal(!modal);

  const { handleSubmit, control, reset } = useForm();

  const handleDelete = async (formData) => {
    try {
      await collectionApis.deleteCollection(
        projectId,
        instanceId,
        name,
        formData
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } finally {
      toggleModal();
      onRefresh();
    }
  };

  const handleDeleteCancel = () => {
    reset();
    toggleModal();
    onClose();
  };

  return (
    <>
      <MenuItem sx={{ color: 'error.main' }} onClick={toggleModal}>
        {t('delete', { ns: 'buttons' })}
      </MenuItem>
      <Modal
        openModal={modal}
        title={t('deletion', {
          ns: 'project',
          text: t('collection', { ns: 'instance' }),
        })}
        handleClose={handleDeleteCancel}
        action={
          <ModalButtons
            onAction={handleSubmit(handleDelete)}
            onCancel={handleDeleteCancel}
            actionProps={{ disabled: form !== name }}
            cancelProps={{ type: 'reset' }}
            actionText={t('delete', { ns: 'buttons' })}
            danger
          />
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('cantUndo', { ns: 'descriptions' })}
        </Alert>
        <Text>
          {t('deleteCollection', { ns: 'descriptions', text: name })}{' '}
          <Trans
            i18nKey="deleteValidation"
            ns="descriptions"
            values={{ validation: name }}
            components={[<StyledSpan key="0" />]}
          />
        </Text>
        <Controller
          name="sc"
          control={control}
          defaultValue={true}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox defaultChecked {...field} />}
              label={
                isSolrCloud
                  ? t('deleteSolrData', { ns: 'instance' })
                  : t('deleteInstanceDir', { ns: 'instance' })
              }
            />
          )}
        />
        {isSolrCloud && (
          <Controller
            name="zc"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox defaultChecked {...field} />}
                label={t('deleteZooKeeperData', { ns: 'instance' })}
              />
            )}
          />
        )}

        <TextField
          variant="outlined"
          label="Deletion Confirmation"
          value={form}
          onChange={handleFormChange}
          fullWidth
          size="small"
          slotProps={{
            htmlInput: {
              'aria-label': 'delete confirmation',
            },
          }}
          sx={{ mt: 3 }}
        />
      </Modal>
    </>
  );
};

DeleteCollectionMenuItem.propTypes = {
  isSolrCloud: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default DeleteCollectionMenuItem;
